import { Decimal } from 'decimal.js';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { RotationCargo } from 'src/types/ViewModel/Stowage/Rotation/RotationCargo';
import { roundTo0Decimal, roundTo3Decimals } from 'src/utils/numberHelper';
import {
    stowedWeightInTanks,
    stowedVolume,
    notStowedWeightInTanks,
    notStowedVolume,
    getCargoOperationStowedTankIds,
} from '../stowageOperation/stowageOperationUtils';
import { getShipFigureForOperationType } from '../stowageOperation/operationDisplayUtil';
import { getCargoOperationWeight, isLoadingOrUnloadingCargoOperation } from '../cargoOperationUtils';
import { getCorrectedLitreWeight } from '../stowageOperation/volumeUtils';
import { getCargoTemperature } from '../stowageOperation/cargoTemperatureUtils';
import { calculateVolume } from '../stowageOperation/volumeUtils';

/**
 * Get formatted litre weight value for a cargo
 * @param stowagePlan - The active stowage plan
 * @param cargo - The rotation cargo
 * @returns Formatted litre weight string
 */
export const getLitreWeightValue = (stowagePlan: StowagePlan, cargo: RotationCargo): string => {
    const cargoTemperature = getCargoTemperature(stowagePlan, cargo);
    return roundTo3Decimals(getCorrectedLitreWeight(cargoTemperature, cargo)?.toNumber() ?? 0).toString();
};

/**
 * Get max adjacent temperature value for a cargo
 * @param cargo - The rotation cargo
 * @returns Max adjacent temperature number
 */
export const getMaxAdjacentTemperatureValue = (cargo: RotationCargo): number =>
    cargo.productChanges?.maxAdjacentTemperature ?? cargo.product.maxAdjacentTemperature;

/**
 * Get formatted stowed weight for a cargo operation
 * Wrapper around existing stowedWeightInTanks utility
 * @param activeStowagePlan - The active stowage plan
 * @param cargoOperation - The cargo operation
 * @returns Formatted stowed weight string or '-' for non-loading/unloading operations
 */
export const getStowedWeight = (activeStowagePlan: StowagePlan, cargoOperation: RotationCargoOperation): string => {
    return isLoadingOrUnloadingCargoOperation(cargoOperation)
        ? roundTo3Decimals(stowedWeightInTanks(activeStowagePlan, cargoOperation).toNumber()).toString()
        : '-';
};

/**
 * Get formatted stowed volume for a cargo operation
 * Wrapper around existing stowedVolume utility
 * @param activeStowagePlan - The active stowage plan
 * @param cargoOperation - The cargo operation
 * @returns Formatted stowed volume string or '-' for non-loading/unloading operations
 */
export const getStowedVolume = (activeStowagePlan: StowagePlan, cargoOperation: RotationCargoOperation): string => {
    return isLoadingOrUnloadingCargoOperation(cargoOperation)
        ? roundTo3Decimals(stowedVolume(activeStowagePlan, cargoOperation).toNumber()).toString()
        : '-';
};

/**
 * Get formatted not stowed weight for a cargo operation
 * Wrapper around existing notStowedWeightInTanks utility
 * @param activeStowagePlan - The active stowage plan
 * @param cargoOperation - The cargo operation
 * @returns Formatted not stowed weight string or '-' for non-loading operations
 */
export const getNotStowedWeightForOperationType = (activeStowagePlan: StowagePlan, cargoOperation: RotationCargoOperation): string | number => {
    if (cargoOperation.operationType !== 'Loading') {
        return '-';
    }

    return roundTo3Decimals(notStowedWeightInTanks(activeStowagePlan, cargoOperation).toNumber()).toString();
};

/**
 * Get formatted not stowed volume for a cargo operation
 * Wrapper around existing notStowedVolume utility
 * @param activeStowagePlan - The active stowage plan
 * @param cargoOperation - The cargo operation
 * @returns Formatted not stowed volume number or '-' for non-loading operations
 */
export const getNotStowedVolumeForOperationType = (activeStowagePlan: StowagePlan, cargoOperation: RotationCargoOperation): string | number => {
    if (cargoOperation.operationType !== 'Loading') {
        return '-';
    }

    return roundTo3Decimals(notStowedVolume(activeStowagePlan, cargoOperation).toNumber());
};

/**
 * Get formatted bill of lading weight
 * @param cargoOperation - The cargo operation
 * @returns Formatted bill of lading weight string or '-'
 */
export const getBillOfLading = (cargoOperation: RotationCargoOperation): string => {
    return cargoOperation.billOfLadingWeight > 0 ? roundTo3Decimals(cargoOperation.billOfLadingWeight).toString() : '-';
};

/**
 * Get formatted nominated weight for a cargo operation
 * @param cargoOperation - The cargo operation
 * @returns Formatted nominated weight string or '-'
 */
export const getNominatedWeightForCargoOperation = (cargoOperation: RotationCargoOperation): string => {
    return cargoOperation.nominatedWeight
        ? roundTo3Decimals(cargoOperation.nominatedWeight * (cargoOperation.operationType === 'Unloading' ? -1 : 1)).toString()
        : '-';
};

/**
 * Get formatted nominated volume for a cargo operation
 * @param activeStowagePlan - The active stowage plan
 * @param cargoOperation - The cargo operation
 * @returns Formatted nominated volume string or '-'
 */
export const getNominatedVolumeForCargoOperation = (activeStowagePlan: StowagePlan, cargoOperation: RotationCargoOperation): string => {
    return cargoOperation.nominatedWeight
        ? roundTo0Decimal(
              calculateVolume(
                  new Decimal(cargoOperation.nominatedWeight * (cargoOperation.operationType === 'Unloading' ? -1 : 1)),
                  cargoOperation.cargo,
                  getCargoTemperature(activeStowagePlan, cargoOperation.cargo)
              ).toNumber()
          ).toString()
        : '-';
};

/**
 * Get formatted ship figure value for a cargo operation
 * Wrapper around existing getShipFigureForOperationType utility
 * @param activeStowagePlan - The active stowage plan
 * @param cargoOperation - The cargo operation
 * @returns Formatted ship figure string or '-'
 */
export const getShipFigureValue = (activeStowagePlan: StowagePlan, cargoOperation: RotationCargoOperation): string => {
    if (cargoOperation.operationType === 'Transfer') return '-';

    const operationTankIds = getCargoOperationStowedTankIds(activeStowagePlan, cargoOperation);
    if (operationTankIds.length === 0) {
        // Nothing has been stowed for this operation
        return '-';
    }

    const shipFigure = getShipFigureForOperationType(activeStowagePlan, cargoOperation);
    // Return '-' when ship figure is zero
    if (shipFigure.isZero()) return '-';

    return roundTo3Decimals(shipFigure.toNumber()).toString();
};

/**
 * Get formatted total loading cargo contract weight value
 * Wrapper around existing getCargoOperationWeight utility
 * @param activeStowagePlan - The active stowage plan
 * @param cargoOperation - The cargo operation
 * @returns Formatted contract weight string
 */
export const getTotalLoadingCargoContractWeightValue = (activeStowagePlan: StowagePlan, cargoOperation: RotationCargoOperation): string => {
    return roundTo0Decimal(getCargoOperationWeight(activeStowagePlan, cargoOperation.cargo, 'Loading', 'contract')).toString();
};
